import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import {
  useClearResearchParticipantMutation,
  useDeleteResearchWorkFileMutation,
  useGetResearchParticipantsByUserQuery,
  useLazyGetResearchFileDownloadUrlQuery,
  useUpdateResearchParticipantStatusMutation
} from '../../../../api/researchApi';
import { selectedUserIdSelector } from '../../../../selectors/userSelectors';
import { convertEnumToTitleCase } from '../../../../utils/stringUtils';

const FILE_TYPES = {
  RESUME: 'RESUME',
  WORK: 'WORK'
};

const REJECT_ACTION = {
  status: 'REJECTED',
  label: 'Reject application',
  variant: 'text',
  color: 'error',
  confirm: true
};

const REQUEST_EDITS_ACTION = {
  status: 'INVITED',
  label: 'Request edits',
  variant: 'outlined',
  color: 'warning',
  requestsRevision: true
};

const STATUS_ACTIONS = {
  NEW: [{ status: 'INVITED', label: 'Move to project', variant: 'contained' }, REJECT_ACTION],
  INVITED: [REJECT_ACTION],
  REJECTED: [{ status: 'NEW', label: 'Reallow application', variant: 'outlined', confirm: true }],
  WORK_SUBMITTED: [{ status: 'WORK_ACCEPTED', label: 'Accept work', variant: 'contained' }, REQUEST_EDITS_ACTION],
  WORK_ACCEPTED: [
    { status: 'PAID', label: 'Mark paid', variant: 'outlined', confirm: true },
    REQUEST_EDITS_ACTION,
    REJECT_ACTION
  ],
  PAID: [REQUEST_EDITS_ACTION, REJECT_ACTION]
};

const STATUS_COLORS = {
  NEW: 'info',
  INVITED: 'primary',
  REJECTED: 'error',
  WORK_SUBMITTED: 'warning',
  WORK_ACCEPTED: 'success',
  PAID: 'success'
};

const getErrorMessage = (error, fallback) => error?.data?.msg || error?.error || fallback;

const FileList = ({ title, emptyMessage, files, onDownload, onDelete, downloadingFileId, deletingFileId }) => (
  <Box>
    <Typography variant='h6' sx={{ mb: 1 }}>
      {title}
    </Typography>
    {files.length === 0 ? (
      <Typography variant='body2' color='text.secondary'>
        {emptyMessage}
      </Typography>
    ) : (
      <List disablePadding>
        {files.map((file) => (
          <ListItem
            key={file.id}
            divider
            secondaryAction={
              <Stack direction='row' sx={{ gap: 1 }}>
                <Button
                  size='small'
                  startIcon={downloadingFileId === file.id ? <CircularProgress size={14} /> : <DownloadIcon />}
                  disabled={downloadingFileId === file.id || deletingFileId === file.id}
                  onClick={() => onDownload(file)}
                >
                  Download
                </Button>
                {onDelete && (
                  <Button
                    size='small'
                    color='error'
                    startIcon={deletingFileId === file.id ? <CircularProgress size={14} /> : <DeleteIcon />}
                    disabled={deletingFileId === file.id || downloadingFileId === file.id}
                    onClick={() => onDelete(file)}
                  >
                    Delete
                  </Button>
                )}
              </Stack>
            }
          >
            <ListItemText
              primary={file.originalFilename}
              secondary={moment(file.createdAt).format('MMM D, YYYY [at] h:mm A')}
              sx={{ pr: onDelete ? 26 : 14 }}
            />
          </ListItem>
        ))}
      </List>
    )}
  </Box>
);

const ResearchParticipant = ({ participant }) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [updateStatus, { isLoading: isUpdatingStatus }] = useUpdateResearchParticipantStatusMutation();
  const [clearParticipant, { isLoading: isClearingParticipant }] = useClearResearchParticipantMutation();
  const [deleteWorkFile] = useDeleteResearchWorkFileMutation();
  const [getDownloadUrl] = useLazyGetResearchFileDownloadUrlQuery();
  const applicationFiles = participant.files.filter(({ type }) => type === FILE_TYPES.RESUME);
  const workFiles = participant.files.filter(({ type }) => type === FILE_TYPES.WORK);
  const isRevisionRequest =
    participant.status === 'INVITED' &&
    (Boolean(participant.revisionNotes) || Boolean(participant.revisionRequestedAt));
  const actions = STATUS_ACTIONS[participant.status] || [];

  const handleStatusChange = async (action) => {
    let revisionNotes;

    if (action.requestsRevision) {
      const result = await Swal.fire({
        icon: 'warning',
        title: 'Request edits?',
        text: 'The participant will see this note and can update their submitted files.',
        input: 'textarea',
        inputLabel: 'Reviewer note',
        inputPlaceholder: 'Describe what needs to change',
        inputAttributes: { maxlength: '5000', 'aria-label': 'Reviewer note' },
        showCancelButton: true,
        confirmButtonText: action.label,
        inputValidator: (value) => (!value.trim() ? 'Add a reviewer note' : undefined)
      });
      if (!result.isConfirmed) {
        return;
      }
      revisionNotes = result.value.trim();
    } else if (action.confirm) {
      const confirmation = await Swal.fire({
        icon: 'warning',
        title: `${action.label}?`,
        showCancelButton: true,
        confirmButtonText: action.label
      });
      if (!confirmation.isConfirmed) {
        return;
      }
    }

    setErrorMessage('');
    try {
      await updateStatus({
        studySlug: participant.study.slug,
        participantId: participant.id,
        status: action.status,
        ...(revisionNotes ? { revisionNotes } : {})
      }).unwrap();
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Could not update the research status.'));
    }
  };

  const handleDownload = async (file) => {
    setErrorMessage('');
    setDownloadingFileId(file.id);
    try {
      const { downloadUrl } = await getDownloadUrl({
        studySlug: participant.study.slug,
        participantId: participant.id,
        fileId: file.id
      }).unwrap();
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Could not download the research file.'));
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handleDeleteFile = async (file) => {
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Delete research file?',
      text: 'This permanently deletes the file.',
      showCancelButton: true,
      confirmButtonText: 'Delete file'
    });
    if (!confirmation.isConfirmed) {
      return;
    }

    setErrorMessage('');
    setDeletingFileId(file.id);
    try {
      await deleteWorkFile({
        studySlug: participant.study.slug,
        participantId: participant.id,
        fileId: file.id
      }).unwrap();
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Could not delete the research file.'));
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleClearApplication = async () => {
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Clear application?',
      text: 'This permanently deletes the application and its uploaded files.',
      showCancelButton: true,
      confirmButtonText: 'Clear application'
    });
    if (!confirmation.isConfirmed) {
      return;
    }

    setErrorMessage('');
    try {
      await clearParticipant({
        studySlug: participant.study.slug,
        participantId: participant.id
      }).unwrap();
    } catch (error) {
      setErrorMessage(getErrorMessage(error, 'Could not clear the research application.'));
    }
  };

  return (
    <Paper variant='outlined' sx={{ p: 2 }}>
      <Stack direction='row' sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant='h6'>{participant.study.slug}</Typography>
          <Typography variant='caption' color='text.secondary'>
            Applied {moment(participant.createdAt).format('MMM D, YYYY')}
          </Typography>
        </Box>
        <Chip
          label={isRevisionRequest ? 'Edits requested' : convertEnumToTitleCase(participant.status)}
          color={isRevisionRequest ? 'warning' : STATUS_COLORS[participant.status]}
        />
      </Stack>

      {errorMessage && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Stack direction='row' sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {actions.map((action) => (
          <Button
            key={action.status}
            color={action.color || 'primary'}
            variant={action.variant}
            disabled={isUpdatingStatus || isClearingParticipant}
            onClick={() => handleStatusChange(action)}
          >
            {action.label}
          </Button>
        ))}
        <Button
          color='error'
          variant='outlined'
          disabled={isUpdatingStatus || isClearingParticipant}
          onClick={handleClearApplication}
        >
          Clear application
        </Button>
      </Stack>

      <Divider sx={{ mb: 2 }} />
      <Stack sx={{ gap: 3 }}>
        {participant.revisionNotes && (
          <Alert severity='warning'>
            <Typography variant='subtitle2'>
              Reviewer note
              {participant.revisionRequestedAt
                ? ` · ${moment(participant.revisionRequestedAt).format('MMM D, YYYY')}`
                : ''}
            </Typography>
            <Typography variant='body2' sx={{ whiteSpace: 'pre-wrap' }}>
              {participant.revisionNotes}
            </Typography>
          </Alert>
        )}
        <Box>
          <Typography variant='h6' sx={{ mb: 1 }}>
            What they do for solo work
          </Typography>
          <Typography
            variant='body2'
            color={participant.applicationAnswer ? 'text.primary' : 'text.secondary'}
            sx={{ whiteSpace: 'pre-wrap' }}
          >
            {participant.applicationAnswer || 'No application answer.'}
          </Typography>
        </Box>
        <FileList
          title='Applicant files'
          emptyMessage='No applicant files.'
          files={applicationFiles}
          onDownload={handleDownload}
          onDelete={handleDeleteFile}
          downloadingFileId={downloadingFileId}
          deletingFileId={deletingFileId}
        />
        <FileList
          title='Work files'
          emptyMessage='No work files submitted.'
          files={workFiles}
          onDownload={handleDownload}
          onDelete={handleDeleteFile}
          downloadingFileId={downloadingFileId}
          deletingFileId={deletingFileId}
        />
      </Stack>
    </Paper>
  );
};

const ResearchSection = () => {
  const selectedUserId = useSelector(selectedUserIdSelector);
  const {
    data: participants = [],
    error,
    isLoading
  } = useGetResearchParticipantsByUserQuery(selectedUserId, { skip: !selectedUserId });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box data-testid='research-section' sx={{ overflowY: 'auto', py: 2 }}>
      {error && <Alert severity='error'>{getErrorMessage(error, 'Could not load research participation.')}</Alert>}
      {!error && participants.length === 0 && <Alert severity='info'>This user has no research participation.</Alert>}
      <Stack sx={{ gap: 2 }}>
        {participants.map((participant) => (
          <ResearchParticipant key={participant.id} participant={participant} />
        ))}
      </Stack>
    </Box>
  );
};

export default ResearchSection;
